import { Avatar, Col, Icon, ModalV2, Row, Text } from 'components';
import { Api, formatLocale, theme, FEATURE_NAMES } from 'lib';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { TableComponents } from 'screens/Admin/components';

const REASON_LABELS = {
  prediction_participation: 'Prediction Vote',
  prediction_bonus: 'Prediction Bonus',
  quiz: 'Quiz',
  potm_vote: 'POTM Vote',
  fancam_live: FEATURE_NAMES.FANCAM_LIVE.en,
  fancam_challenge: 'FanCam Challenge',
  live_check_in: 'Live Check-in',
  daily_like_streak: 'Daily Like Streak',
  redeem: 'Redeem',
  admin_adjustment: 'Admin Adjustment'
};

const SUBTYPE_LABELS = {
  half_time: 'Half Time',
  full_time: 'Full Time',
  photo: 'Photo',
  video: 'Video'
};

const getReasonLabel = (reason, subType) => {
  const label = REASON_LABELS[reason] || reason;
  if (subType && SUBTYPE_LABELS[subType]) {
    return `${label} (${SUBTYPE_LABELS[subType]})`;
  }
  return label;
};

const BadgeContainer = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 16px;
  background: ${theme.GREY_10};
  gap: 6px;
`;

const StatBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 20px;
  background: ${theme.GREY_10};
  border-radius: 10px;
  min-width: 100px;
`;

const RewardHistoryModal = ({ teamId, user, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [badge, setBadge] = useState(null);
  const LIMIT = 20;

  useEffect(() => {
    if (teamId && user?._id) {
      fetchHistory();
    }
  }, [teamId, user]);

  const fetchHistory = async (newOffset = 0) => {
    setLoading(true);
    try {
      const res = await Api.get(
        `/admin/rewardHistories/list?teamId=${teamId}&userId=${user.userId}&offset=${newOffset}&limit=${LIMIT}`
      );
      if (res?.data) {
        setData(res.data.items || []);
        setTotalPoints(res.data.totalPoints || 0);
        setBadge(res.data.badge || null);
        setTotal(res.pagination?.total || 0);
        setOffset(res.pagination?.offset || 0);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handlePageChange = (direction) => {
    const newOffset = direction === 'next' ? offset + LIMIT : offset - LIMIT;
    if (newOffset >= 0 && newOffset < total) {
      fetchHistory(newOffset);
    }
  };

  return (
    <ModalV2
      visible={true}
      closeModal={onClose}
      width="900px"
      header={
        <Row between center wid="100%">
          <Row center>
            <Avatar user={user} avatar={user.avatar} size={40} />
            <Col marg="0 0 0 12px">
              <Text h3>Reward History</Text>
              <Text b2 col={theme.GREY_60}>
                {user.name} (@{user.username})
              </Text>
            </Col>
          </Row>
          <Row center gap="16px" marg="0 40px 0 0">
            <StatBox>
              <Text h4 bold col={theme.PRIMARY}>{totalPoints}</Text>
              <Text c2 col={theme.GREY_60}>Total Points</Text>
            </StatBox>
            {badge?.currentTier && (
              <StatBox>
                <Text h4 bold>{badge.currentTier}</Text>
                <Text c2 col={theme.GREY_60}>
                  {badge.progress || 'Current Tier'}
                </Text>
              </StatBox>
            )}
          </Row>
        </Row>
      }
      footer={
        total > LIMIT ? (
          <Row center end>
            <div
              onClick={() => handlePageChange('prev')}
              style={{
                cursor: offset > 0 ? 'pointer' : 'not-allowed',
                opacity: offset > 0 ? 1 : 0.5,
                marginRight: 20
              }}
            >
              <Icon type="arrowLeft" />
            </div>
            <Text>
              {offset + 1} - {Math.min(offset + LIMIT, total)} of {total}
            </Text>
            <div
              onClick={() => handlePageChange('next')}
              style={{
                cursor: offset + LIMIT < total ? 'pointer' : 'not-allowed',
                opacity: offset + LIMIT < total ? 1 : 0.5,
                marginLeft: 20
              }}
            >
              <Icon type="arrowRight" />
            </div>
          </Row>
        ) : null
      }
    >
      <Col pad="unset">
        <TableComponents.List
          loading={loading}
          Header={() => (
            <React.Fragment>
              <TableComponents.Th first>
                <Text>Date</Text>
              </TableComponents.Th>
              <TableComponents.Th>
                <Text>Action</Text>
              </TableComponents.Th>
              <TableComponents.Th>
                <Text>Amount</Text>
              </TableComponents.Th>
              <TableComponents.Th>
                <Text>Balance</Text>
              </TableComponents.Th>
              <TableComponents.Th last>
                <Text>Description</Text>
              </TableComponents.Th>
            </React.Fragment>
          )}
          Items={() =>
            data.map((item) => (
              <TableComponents.Tr key={item._id}>
                <TableComponents.Td first>
                  <Text b2>{formatLocale(item.createdAt, 'dd MMM yyyy HH:mm')}</Text>
                </TableComponents.Td>
                <TableComponents.Td>
                  <Text b2>{getReasonLabel(item.reason, item.subType)}</Text>
                </TableComponents.Td>
                <TableComponents.Td>
                  <Text b2 med col={item.amount > 0 ? theme.GREEN : theme.RED}>
                    {item.amount > 0 ? '+' : ''}
                    {item.amount}
                  </Text>
                </TableComponents.Td>
                <TableComponents.Td>
                  <Text b2>{item.balanceAfter}</Text>
                </TableComponents.Td>
                <TableComponents.Td last>
                  <Text b2 tiny col={theme.GREY_60}>
                    {item.description || '-'}
                  </Text>
                </TableComponents.Td>
              </TableComponents.Tr>
            ))
          }
        />
      </Col>
    </ModalV2>
  );
};

export default RewardHistoryModal;
